import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/api';

export type CommitteePaperFilter = 'ALL' | 'PENDING' | 'UNDER_EVAL' | 'APPROVED' | 'REJECTED';
export type CommitteePaperProcessFilter =
  | 'ALL'
  | 'APPROVED_TO_PAY'
  | 'PAID'
  | 'MATERIAL_UPLOADED'
  | 'CREDENTIAL_ISSUED';
export type CommitteePaperNationalityFilter = 'ALL' | 'MEXICAN' | 'FOREIGN';
export type CommitteePaperStatus =
  | 'PENDING'
  | 'PENDING_REVIEW'
  | 'REVISION_REQUESTED'
  | 'UNDER_EVALUATION'
  | 'APPROVED_TO_PAY'
  | 'PAID'
  | 'MATERIAL_UPLOADED'
  | 'CREDENTIAL_ISSUED'
  | 'REJECTED';

export interface CommitteePaperItem {
  id: string;
  folio: string;
  title: string;
  author: {
    name: string;
    institution: string;
  };
  academicUnit: string;
  status: CommitteePaperStatus;
  registrationStatus: string;
  materialTokenExpiresAt?: string | null;
  evaluatorsCount: number;
  evaluatorsMax: number;
  evaluationId: string | null;
  evaluationStatus: string | null;
  evaluationRound: number | null;
  hasCleanVersion: boolean;
  documentStatus?: 'ORIGINAL' | 'CLEAN_AVAILABLE' | 'PENDING_CLEAN' | null;
  latestCleanVersionNumber?: number | null;
  latestRevisionVersionNumber?: number | null;
  canStartSecondRound?: boolean;
  secondRoundStartReason?: string | null;
  requiresCleanForAssignment?: boolean;
  canAssignEvaluators?: boolean;
  assignDisabledReason?: string | null;
  document?: {
    status?: 'ORIGINAL' | 'CLEAN_AVAILABLE' | 'PENDING_CLEAN' | null;
    hasCleanVersion?: boolean;
    latestCleanVersionNumber?: number | null;
  } | null;
  assignmentPolicy?: {
    requireCleanPaper?: boolean;
    canAssign?: boolean;
    reason?: string | null;
  } | null;
  evaluators: Array<{
    id: string;
    name: string;
    email: string;
    tokenExpiresAt?: string | null;
    tokenUsedAt?: string | null;
    round1DecisionId?: string | null;
    round2DecisionId?: string | null;
    hasDecision: boolean;
    hasCurrentRoundDecision?: boolean;
    canResendEvaluationLink?: boolean;
  }>;
  createdAt: string;
}

export type CommitteePaperVersionType = 'ORIGINAL' | 'CLEAN' | 'REVISION';

export interface CommitteePaperVersionItem {
  id: string;
  versionNumber: number;
  versionType: CommitteePaperVersionType;
  roundNumber: number | null;
  archivoNombre: string | null;
  archivoMime: string | null;
  uploadedBy: string | null;
  uploadedAt: string;
  isActive: boolean;
  isLegacyOriginal?: boolean;
  downloadUrl: string;
}

export interface CommitteeEvaluatorAvailableItem {
  id: string;
  memberId: string;
  name: string;
  email: string;
  institution: string | null;
  currentLoad: number;
  isAtMaxLoad: boolean;
  status: 'ACTIVE';
  selected: boolean;
}

export interface ReassignImpactPreview {
  inProgressCount: number;
  currentNames: string[];
  newNames: string[];
  requiresConfirmation: boolean;
}

interface PapersQueryParams {
  eventId: string;
  filter: CommitteePaperFilter;
  processStatus: CommitteePaperProcessFilter;
  nationality: CommitteePaperNationalityFilter;
  search: string;
}

export interface OperationalSpeakersExportParams {
  eventId: string;
  tab: CommitteePaperFilter;
  processStatus: CommitteePaperProcessFilter;
  nationality: CommitteePaperNationalityFilter;
  search: string;
}

async function fetchCommitteePapers(params: PapersQueryParams): Promise<CommitteePaperItem[]> {
  const { data } = await api.get('/committee/papers', {
    params: {
      eventId: params.eventId,
      filter: params.filter,
      processStatus: params.processStatus === 'ALL' ? undefined : params.processStatus,
      nationality: params.nationality === 'ALL' ? undefined : params.nationality,
      search: params.search.trim().length >= 3 ? params.search.trim().slice(0, 100) : undefined,
    },
  });

  if (!Array.isArray(data?.papers)) {
    return [];
  }

  return data.papers as CommitteePaperItem[];
}

async function fetchPaperVersions(paperId: string): Promise<CommitteePaperVersionItem[]> {
  const { data } = await api.get(`/committee/papers/${paperId}/versions`);
  if (!Array.isArray(data?.versions)) {
    return [];
  }
  return data.versions as CommitteePaperVersionItem[];
}

async function fetchAvailableEvaluators(eventId: string, paperId: string): Promise<CommitteeEvaluatorAvailableItem[]> {
  const { data } = await api.get('/committee/evaluators/available', {
    params: { eventId, paperId },
  });
  if (!Array.isArray(data?.evaluators)) {
    return [];
  }
  return data.evaluators as CommitteeEvaluatorAvailableItem[];
}

export function useCommitteePapers(
  eventId: string,
  filter: CommitteePaperFilter,
  processStatus: CommitteePaperProcessFilter,
  nationality: CommitteePaperNationalityFilter,
  search: string,
) {
  return useQuery({
    queryKey: ['committee-papers', eventId, filter, processStatus, nationality, search],
    queryFn: () => fetchCommitteePapers({ eventId, filter, processStatus, nationality, search }),
    enabled: Boolean(eventId),
    staleTime: 30_000,
  });
}

export function useExportFinalSpeakers() {
  return useMutation({
    mutationFn: async (eventId: string) => {
      const response = await api.get('/committee/papers/export-final', {
        params: { eventId },
        responseType: 'blob',
      });

      const disposition = String(response.headers['content-disposition'] || '');
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `ponentes-finales-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);

      return { filename };
    },
  });
}

export function useExportOperationalSpeakers() {
  return useMutation({
    mutationFn: async (params: OperationalSpeakersExportParams) => {
      const normalizedSearch = params.search.trim();
      const response = await api.get('/committee/papers/export-operational', {
        params: {
          eventId: params.eventId,
          tab: params.tab,
          processStatus: params.processStatus,
          nationality: params.nationality,
          search: normalizedSearch.length >= 3 ? normalizedSearch.slice(0, 100) : undefined,
        },
        responseType: 'blob',
      });

      const disposition = String(response.headers['content-disposition'] || '');
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1]
        || `ponentes-operativos-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);

      return { filename };
    },
  });
}

export function useAvailableEvaluators(eventId: string, paperId: string, enabled = true) {
  return useQuery({
    queryKey: ['committee-evaluators-available', eventId, paperId],
    queryFn: () => fetchAvailableEvaluators(eventId, paperId),
    enabled: Boolean(eventId && paperId && enabled),
    staleTime: 30_000,
  });
}

export function useCommitteePaperVersions(paperId: string, enabled = true) {
  return useQuery({
    queryKey: ['committee-paper-versions', paperId],
    queryFn: () => fetchPaperVersions(paperId),
    enabled: Boolean(paperId && enabled),
    staleTime: 10_000,
  });
}

export function useUploadCleanVersion(_eventId: string, paperId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/committee/papers/${paperId}/upload-clean`, formData);
      return data;
    },
    onSuccess: async (data) => {
      const uploadedVersionNumber =
        typeof data?.version?.versionNumber === 'number' ? data.version.versionNumber : null;

      queryClient.setQueriesData<CommitteePaperItem[]>({ queryKey: ['committee-papers'] }, (current) => {
        if (!Array.isArray(current)) return current;

        return current.map((paper) => {
          if (paper.id !== paperId) return paper;

          return {
            ...paper,
            documentStatus: 'CLEAN_AVAILABLE',
            hasCleanVersion: true,
            latestCleanVersionNumber: uploadedVersionNumber ?? paper.latestCleanVersionNumber ?? null,
            document: {
              ...(paper.document || {}),
              status: 'CLEAN_AVAILABLE',
              hasCleanVersion: true,
              latestCleanVersionNumber: uploadedVersionNumber ?? paper.document?.latestCleanVersionNumber ?? null,
            },
          };
        });
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['committee-paper-versions', paperId] }),
        queryClient.invalidateQueries({ queryKey: ['committee-papers'] }),
        queryClient.refetchQueries({ queryKey: ['committee-papers'], type: 'active' }),
      ]);
    },
  });
}

export function useAssignEvaluators(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ paperId, evaluatorIds }: { paperId: string; evaluatorIds: string[] }) =>
      api.post(`/committee/papers/${paperId}/assign`, { evaluatorIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}

export function useReassignImpact() {
  return useMutation({
    mutationFn: ({ paperId, evaluatorIds }: { paperId: string; evaluatorIds: string[] }) =>
      api.post(`/committee/papers/${paperId}/reassign/evaluate-impact`, {
        newEvaluatorIds: evaluatorIds,
      }),
  });
}

export function useReassignEvaluators(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      paperId,
      evaluatorIds,
      confirmImpact,
    }: {
      paperId: string;
      evaluatorIds: string[];
      confirmImpact: boolean;
    }) =>
      api.put(`/committee/papers/${paperId}/assign`, {
        evaluatorIds,
        confirmImpact,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}
export function useStartSecondRound(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paperId }: { paperId: string }) => {
      const { data } = await api.post(`/committee/papers/${paperId}/start-round`);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}

export function useResendEvaluatorMagicLink(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paperId, assignmentId }: { paperId: string; assignmentId: string }) => {
      const { data } = await api.post(`/committee/papers/${paperId}/assignments/${assignmentId}/resend`);
      return data as {
        success: boolean;
        message: string;
        actionPerformed?: 'RESENT_EXISTING_TOKEN' | 'RENEWED_AND_RESENT';
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['committee-papers', eventId] });
    },
  });
}
